import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { LanguageModule } from '@language';
import { flatMapDeep } from 'lodash-es';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, take, takeUntil, tap } from 'rxjs/operators';
import { QueryPartStrategy } from '../../../../advanced-search/filters/base/entities/query-part-strategy';
import { QueryPart } from '../../../../advanced-search/filters/base/entities/query-part.class';
import { BaseContainer } from '../../../../core/common/containers/base.abstract.container';
import { AdvancedSearch, AdvancedSearchGroup } from '../../../../core/query/entities/structured-query.entity';
import { QueryPartsToStructuredQuery } from '../../../../core/query/helpers/structured-query.helper';
import { ShUrlQueryService } from '../../../../core/query/services/url-query.service';
import { TreeNode } from '../../../../elements/components/tree-view/entities/tree-viewer-node.entity';
import {
    ITreeViewerNode,
    ShTreeViewerComponent,
    TreeViewerNode
} from '../../../../elements/components/tree-view/tree-viewer.component';
import { PaginatedResults } from '../../../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import { RecordTreeEntity } from '../../entities/record-tree.entity';
import { ShRecordTreeSandbox } from '../../sandboxes/record-tree-sandbox.service';

@Component({
    standalone: true,
    selector: 'sh-record-tree-panel',
    templateUrl: './record-tree-panel-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, LanguageModule, ShTreeViewerComponent],
    providers: [ShRecordTreeSandbox]
})
export class ShRecordTreePanelContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    public recordTreeNodes$!: Observable<TreeViewerNode<RecordTreeEntity>[]>;
    private nodesUpdated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public selectedNode: ITreeViewerNode<RecordTreeEntity> | undefined;
    private currentNodes: TreeViewerNode<RecordTreeEntity>[];

    private readonly urlQueryService: ShUrlQueryService = inject(ShUrlQueryService);
    private readonly recordTreeService: ShRecordTreeSandbox = inject(ShRecordTreeSandbox);

    ngOnInit(): void {
        this.initRecordTree();
    }

    private initRecordTree() {
        // Optional: Based on current page/query and backgroundfiltering
        // For now expect to always show complete recordTree

        this.loading$.next(true);
        const rootNodes$: Observable<TreeViewerNode<RecordTreeEntity>[]> = this.recordTreeService
            .getRecordTree()
            .pipe(
                map(recordTree =>
                    recordTree.results.map(recordTreeEntity => this.recordTreeEnitityToNode(recordTreeEntity))
                )
            );

        // TODO: Loading state
        this.recordTreeNodes$ = combineLatest([this.nodesUpdated$, rootNodes$]).pipe(
            map(([_, rootNodes]) => this.currentNodes ?? rootNodes),
            tap(() => this.loading$.next(false))
        );
    }

    private recordTreeEnitityToNode(RecordTreeEntity: RecordTreeEntity): TreeViewerNode<RecordTreeEntity> {
        const label: string = RecordTreeEntity.referenceTitle;

        const isActive: boolean = RecordTreeEntity.referenceCode === this.selectedNode?.data?.referenceCode;

        const hasChildren: boolean = RecordTreeEntity.hasChildren;

        return {
            id: RecordTreeEntity.referenceCode,
            name: label,
            data: RecordTreeEntity,
            hasChildren: hasChildren,
            isActive
        };
    }

    public getNodeNarrowerRecordTree = (
        node: TreeNode,
        offset = 0
    ): Observable<PaginatedResults<TreeViewerNode<RecordTreeEntity>>> => {
        return this.getNarrowerrecordTreeNodes(node.data.data, offset);
    };

    private getNarrowerrecordTreeNodes(
        RecordTreeEntity: RecordTreeEntity,
        offset = 0
    ): Observable<PaginatedResults<TreeViewerNode<RecordTreeEntity>>> {
        return this.recordTreeService.getRecordTree(RecordTreeEntity, offset).pipe(
            map(paginatedResults => ({
                ...paginatedResults,
                results: paginatedResults.results.map(recordTree => this.recordTreeEnitityToNode(recordTree))
            }))
        );
    }

    // Optional: Set current selection from URLQueryService

    public onSelectedNodes(selectedNodes: ITreeViewerNode<RecordTreeEntity>[]) {
        if (this.selectedNode) {
            // unset previous
            this.toggleConceptNodeState(this.selectedNode.data, false);
        }

        if (selectedNodes.length == 0) {
            this.selectedNode = undefined;
            this.removeReferenceCodeFromQuery();
        } else {
            this.selectedNode = selectedNodes[0];
            this.addReferenceCodeToQuery(this.selectedNode.data);
        }

        if (this.selectedNode) {
            // set new
            this.toggleConceptNodeState(this.selectedNode.data, true);
        }
    }

    private removeReferenceCodeFromQuery() {
        this.urlQueryService
            .getStructuredQuery()
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(structuredQuery => {
                const referenceCodeField = `Structural.ReferenceCodes`;

                const advancedSearch = structuredQuery.AdvancedSearch;
                const filteredGroups: AdvancedSearchGroup[] = advancedSearch.Groups.map(group => {
                    const filteredTerms = group.Terms?.filter(term => term.DottedKey.startsWith(referenceCodeField));

                    return {
                        ...group,
                        Terms: filteredTerms
                    };
                });

                const filteredAdvancedSearch: AdvancedSearch = {
                    ...advancedSearch,
                    Groups: filteredGroups
                };
                const filteredStructuredQuery = {
                    ...structuredQuery,
                    AdvancedSearch: filteredAdvancedSearch
                };
                this.urlQueryService.setAdvancedSearchQueryParam(filteredStructuredQuery);
            });
    }

    private addReferenceCodeToQuery(entity: RecordTreeEntity): void {
        // might want to just add an 'Add to query' method to UrlQueryService
        const field: string = `Structural.ReferenceCodes.${entity.referenceType}`;

        const queryPart: QueryPart = new QueryPart({
            field: field,
            strategy: QueryPartStrategy.MatchAtLeastOne,
            values: [entity.referenceCode]
        });

        // TODO Add OR for parent itself...
        // NOTE: Is not possible through url + downside it will show up in advanced-search
        // Can we add this to the records-sandboxes?
        this.urlQueryService.setAdvancedSearchQueryParam(QueryPartsToStructuredQuery([queryPart]));
    }

    // Maybe refactor
    protected toggleConceptNodeState(concept: RecordTreeEntity | null, state: boolean): boolean {
        const nodeToActivate: TreeViewerNode<RecordTreeEntity> | undefined = this.findTreeNodeById(
            this.currentNodes,
            concept?.referenceCode as string
        ) as TreeViewerNode<RecordTreeEntity> | undefined;

        if (!nodeToActivate || nodeToActivate.isActive === state) {
            return false;
        }

        nodeToActivate.isActive = state;
        return true;
    }

    protected findTreeNodeById(
        treeNodes: TreeViewerNode<RecordTreeEntity>[],
        conceptUri: string
    ): ITreeViewerNode<RecordTreeEntity> | undefined {
        const flattenedNodes: ITreeViewerNode<RecordTreeEntity>[] = flatMapDeep(treeNodes, this.getChildren);
        return flattenedNodes.find(node => node.id === conceptUri);
    }

    protected getChildren: any = (child: TreeViewerNode<RecordTreeEntity>) => {
        if (!child.children || !child.children.length) {
            return child;
        }
        return [child, flatMapDeep(child.children, this.getChildren)];
    };

    public onUpdatedNodes(updatedNodes: ITreeViewerNode<RecordTreeEntity>[]) {
        this.currentNodes = updatedNodes;
    }
}
